<template>
  <div class="container">
    <div class="cta-section">
      <h3>Get started today</h3>
      <p class="starter-line">Transform your enterprise with Spatial AI</p>
      <button class="cta-button" @click="contactUs">Contact us</button>
    </div>
  </div>

  <!-- Contact Form Modal -->
  <div v-if="showContactForm" class="modal-overlay" @click="closeContactForm">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>Contact Sales</h3>
        <button class="close-btn" @click="closeContactForm">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <form @submit.prevent="submitForm" class="contact-form">
        <div class="form-group" id="name">
          <label for="name">Name *</label>
          <input type="text" id="name" v-model="form.name" required />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="email">Work Email *</label>
            <input type="email" id="email" v-model="form.email" required />
          </div>
          <div class="form-group">
            <label for="phone">Phone Number</label>
            <div class="phone-input-group">
              <div class="country-code-select">
                <b>+</b>
                <input
                  v-model="countryCode"
                  type="text"
                  inputmode="numeric"
                  aria-label="Country code"
                  maxlength="3"
                  minlength="0"
                />
              </div>
              <input
                type="tel"
                id="phone"
                pattern="[0-9].{4,}"
                minlength="5"
                maxlength="10"
                v-model="form.contactNumber"
                class="phone-number-input"
              />
            </div>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="company">Company Name *</label>
            <input
              type="text"
              id="company"
              v-model="form.companyName"
              required
            />
          </div>
          <div class="form-group">
            <label for="jobTitle">Job Title</label>
            <input type="text" id="jobTitle" v-model="form.jobTitle" />
          </div>
        </div>
        <div class="form-group">
          <label for="companySize">Company Size *</label>
          <select id="companySize" v-model="form.companySize" required>
            <option value="">Select company size</option>
            <option v-for="size in CompanySizeArr" :value="size">
              {{ size }} employees
            </option>
          </select>
        </div>
        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="closeContactForm">
            Cancel
          </button>
          <button type="submit" class="submit-btn">Send Message</button>
        </div>
        <div
          v-show="flash"
          id="simple-msg"
          class="p-2"
          role="status"
          aria-live="polite"
        >
          {{ msg }}
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import axios from "axios";
const showContactForm = ref(false);
const config = useRuntimeConfig();
let CompanySizeArr = ["1-10", "11-50", "51-200", "201-1000", "1000+"];

const form = reactive({
  name: "",
  email: "",
  contactNumber: "",
  companyName: "",
  jobTitle: "",
  companySize: "",
});
const msg = ref(null);
const flash = ref(false);
const submit = ref(false);
const countryCode = ref(1);

const openContactForm = () => {
  showContactForm.value = true;
  document.body.style.overflow = "hidden";
};

const closeContactForm = () => {
  showContactForm.value = false;
  document.body.style.overflow = "auto";
};

const contactUs = () => {
  openContactForm();
};
const submitForm = async () => {
  try {
    const mobileRegix = /^\+(?:[0-9] ?){6,14}[0-9]$/;
    const mobilenumber = `+${countryCode.value}${form.contactNumber}`;

    if (!(form.name && form.email && form.companyName && form.companySize))
      return;
    if (form.contactNumber) {
      if (!mobileRegix.test(mobilenumber)) {
        flash.value = true;
        msg.value = "please enter valid mobile number";
        return;
      }
      flash.value = false;
    }
    //queue check
    if (submit.value) return;
    submit.value = true;
    const url = config.public.NUXT_API_URL + "/contact";
    const response = await axios.post(url, {
      name: form.name,
      email: form.email,
      mobile: mobilenumber,
      message: `
       company : ${form.companyName}
       jobTitle : ${form.jobTitle}
       companySize : ${form.companySize}
       msg: for enterprise demo request
      `,
    });
    if (response.status == 200) {
      flash.value = true;
      msg.value = "Message sent successfully";
      setTimeout(() => {
        closeContactForm();
      }, 3000);
      submit.value = false;
      form.name = "";
      form.email = "";
      form.contactNumber = "";
      form.companyName = "";
      form.jobTitle = "";
      form.companySize = "";
    }
  } catch (error) {
    flash.value = true;
    msg.value = "Something went wrong, please try again later.";
    submit.value = false;
  }
};

const handleEscape = (e) => {
  if (e.key === "Escape" && showContactForm.value) {
    closeContactForm();
  }
};
onMounted(() => {
  document.addEventListener("keydown", handleEscape);
});
onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  document.body.style.overflow = "auto";
});
</script>
<style lang="scss" scoped>
.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 4rem;
  width: 100%;

  @include respond-below(lg) {
    padding: 0 3rem;
  }

  @include respond-below(sm) {
    padding: 0 1rem;
  }
}
.cta-section {
  margin-top: 4rem;
  text-align: center;
  padding: 3rem 2rem;
  background: rgba(5, 5, 5, 0.1);
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    font-size: 2.5rem;
    font-weight: $fw-bold;
    background-image: radial-gradient(
      47.08% 208.33% at 79.71% 128.33%,
      rgba(237, 125, 255, 0.8) 11.69%,
      rgba(108, 84, 255, 0.8) 35.44%,
      rgba(249, 245, 245, 0.8) 70.24%
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 1rem 0;

    @include respond-below(sm) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 2rem 0;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 2rem;

    @include respond-below(sm) {
      font-size: 1rem;
    }
  }

  .cta-button {
    background: inherit;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 1rem 2.5rem;
    font-size: 1.1rem;
    font-weight: $fw-semibold;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 15px 30px rgba(138, 43, 226, 0.4);
      border: 1px solid rgba(138, 43, 226, 0.3);
    }
  }
}

// Modal Styles
.modal-overlay {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(5px);

  @include respond-below(sm) {
    padding: 1rem;
  }
}

.modal-content {
  background: #1a1a1a;
  border-radius: $radius-lg;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);

  @include respond-below(sm) {
    max-height: 95vh;
  }
}

.modal-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(sm) {
    padding: 1.5rem 1.5rem 1rem;
  }

  h3 {
    margin: 0;
    font-size: 2.8rem;
    font-weight: $fw-semibold;
    color: white;

    @include respond-below(sm) {
      font-size: $text-lg;
    }
  }

  .close-btn {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    padding: 0.5rem;
    border-radius: $radius-sm;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    i {
      font-size: 1.5rem;
    }
  }
}

.contact-form {
  padding: 2rem;

  @include respond-below(sm) {
    padding: 1.5rem;
  }
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @include respond-below(sm) {
    grid-template-columns: 1fr;
    gap: 0;
  }
}

#name {
  width: 100%;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: $fw-medium;
    font-size: $text-sm;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: $radius-sm;
    color: white;
    font-family: $theme-font;
    font-size: $text-sm;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #8a2be2;
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  select {
    cursor: pointer;

    option {
      background: #1a1a1a;
      color: white;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  // Phone input group styles
  .phone-input-group {
    display: flex;
    gap: 0.5rem;

    .country-code-select {
      display: flex;
      align-items: center;
      min-width: 70px;
      max-width: 70px;
      padding: 0.75rem 0.5rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-sm;
      color: white;
      cursor: pointer;
      appearance: none;
      input {
        border: none;
        background: transparent;
        color: white;
        width: 100%;
        font-family: $theme-font;
        font-size: inherit;
        padding: 0;
        margin-left: 0.25rem;
        box-shadow: none;
        @include respond-below(sm) {
          padding: 0.5rem;
        }
      }

      @include respond-below(md) {
        min-width: 50px;
        max-width: 50px;
        font-size: $text-xxs;
        padding: 0.625rem 0.375rem;
      }

      @include respond-below(sm) {
        min-width: 30px;
        max-width: 30px;
        padding: 0.5rem 0.25rem;
        background-size: 6px;
      }

      &:focus-within {
        border-color: #8a2be2;
        background: rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
      }
    }

    .phone-number-input {
      flex: 1;
      padding: 0.75rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-sm;
      color: white;
      font-family: $theme-font;
      font-size: $text-sm;
      transition: all 0.2s ease;

      @include respond-below(md) {
        padding: 0.625rem;
        font-size: $text-xs;
      }

      @include respond-below(sm) {
        padding: 0.5rem;
        font-size: $text-xxs;
      }

      &:focus {
        border-color: #8a2be2;
        background: rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
        font-size: inherit;
      }
    }

    @include respond-below(xs) {
      // flex-direction: column;
      gap: 0.25rem;

      .country-code-select {
        min-width: 20%;
        // max-width: unset;
      }
    }
  }
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(sm) {
    flex-direction: column;
  }

  button {
    padding: 0.75rem 1.5rem;
    border-radius: $radius-sm;
    font-weight: $fw-medium;
    font-size: $text-sm;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $theme-font;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .cancel-btn {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }
  }

  .submit-btn {
    background: #8a2be2;
    border: 1px solid #8a2be2;
    color: white;

    &:hover:not(:disabled) {
      background: #7a25c7;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(138, 43, 226, 0.3);
    }
  }
}
</style>
